import { persistentCache } from '../services/persistentCache';

const MAX_ITEMS = 40;
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

interface StoredPayload {
  payload: string;
  createdAt: number;
}

export type ImagePayloadLookupStatus = 'found' | 'missing' | 'unavailable';

export interface ImagePayloadLookupResult {
  payload: string | null;
  status: ImagePayloadLookupStatus;
}

export interface StoredImagePayloadToken {
  token: string;
  persistence: 'persistent' | 'volatile';
}

const volatilePayloadStore = new Map<string, StoredPayload>();

const now = (): number => Date.now();
const normalizeScopeId = (scopeId?: string | null): string => (
  String(scopeId || '').trim() || '__anonymous__'
);
const volatilePayloadKey = (
  token: string,
  scopeId?: string | null,
): string => `${normalizeScopeId(scopeId)}::${token}`;

const randomToken = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }

  return `${Math.random().toString(36).slice(2)}${now().toString(36)}`;
};

const isExpired = (createdAt: number): boolean => now() - createdAt > MAX_AGE_MS;

const pruneVolatileStore = (): void => {
  const freshEntries = Array.from(volatilePayloadStore.entries())
    .filter(([, item]) => !isExpired(item.createdAt))
    .sort((a, b) => a[1].createdAt - b[1].createdAt);

  volatilePayloadStore.clear();
  freshEntries.slice(Math.max(freshEntries.length - MAX_ITEMS, 0)).forEach(([token, item]) => {
    volatilePayloadStore.set(token, item);
  });
};

export const clearImagePayloadMemoryStore = (): void => {
  volatilePayloadStore.clear();
};

export const storeImagePayload = async (
  payload: string,
  scopeId?: string | null,
): Promise<StoredImagePayloadToken> => {
  const token = randomToken();
  const createdAt = now();
  const item: StoredPayload = { payload, createdAt };
  volatilePayloadStore.set(volatilePayloadKey(token, scopeId), item);
  pruneVolatileStore();

  const normalizedScopeId = normalizeScopeId(scopeId);
  const persisted = await persistentCache.setSearchImagePayload(
    token,
    payload,
    createdAt,
    normalizedScopeId,
  );
  if (!persisted) {
    return { token, persistence: 'volatile' };
  }

  await persistentCache.pruneSearchImagePayloads(
    MAX_ITEMS,
    MAX_AGE_MS,
    normalizedScopeId,
  );
  return { token, persistence: 'persistent' };
};

export const getImagePayloadWithStatus = async (
  token: string | null | undefined,
  scopeId?: string | null,
): Promise<ImagePayloadLookupResult> => {
  if (!token) {
    return {
      payload: null,
      status: 'missing',
    };
  }

  const volatileKey = volatilePayloadKey(token, scopeId);
  const volatileItem = volatilePayloadStore.get(volatileKey);
  if (volatileItem) {
    if (isExpired(volatileItem.createdAt)) {
      volatilePayloadStore.delete(volatileKey);
      await persistentCache.removeSearchImagePayload(
        token,
        normalizeScopeId(scopeId),
      );
      return {
        payload: null,
        status: 'missing',
      };
    }
    return {
      payload: volatileItem.payload,
      status: 'found',
    };
  }

  const persistentLookup = await persistentCache.getSearchImagePayload(
    token,
    normalizeScopeId(scopeId),
  );
  if (persistentLookup.status === 'unavailable') {
    return {
      payload: null,
      status: 'unavailable',
    };
  }
  if (persistentLookup.status === 'missing') {
    return {
      payload: null,
      status: 'missing',
    };
  }

  if (isExpired(persistentLookup.item.createdAt)) {
    await removeImagePayload(token, scopeId);
    return {
      payload: null,
      status: 'missing',
    };
  }

  volatilePayloadStore.set(volatileKey, persistentLookup.item);
  pruneVolatileStore();
  return {
    payload: persistentLookup.item.payload,
    status: 'found',
  };
};

export const getImagePayload = async (
  token: string | null | undefined,
  scopeId?: string | null,
): Promise<string | null> => {
  const lookup = await getImagePayloadWithStatus(token, scopeId);
  return lookup.payload;
};

export const removeImagePayload = async (
  token: string,
  scopeId?: string | null,
): Promise<void> => {
  if (!token) return;
  volatilePayloadStore.delete(volatilePayloadKey(token, scopeId));
  await persistentCache.removeSearchImagePayload(
    token,
    normalizeScopeId(scopeId),
  );
};
